/**
 * StickyProductMedia - Sticky wrapper with in-place zoom functionality
 * 
 * This implementation provides CSS sticky positioning and adds an in-place 
 * zoom feature that follows mouse movement within the image container.
 */

class StickyProductMedia extends HTMLElement {
  constructor() {
    super();
    this.isInPlaceZoomActive = false;
    this.zoomLevel = 2;
    this.currentImage = null;
  }
  
  connectedCallback() {
    // Add sticky class immediately for layout
    this.classList.add('sticky-enabled');
    
    // Defer heavy operations until browser is idle to improve page load speed
    if (window.requestIdleCallback) {
      requestIdleCallback(() => {
        this._setupInPlaceZoom();
        this._ensurePhotoSwipeZIndex();
      });
    } else {
      // Fallback for browsers without requestIdleCallback
      setTimeout(() => {
        this._setupInPlaceZoom();
        this._ensurePhotoSwipeZIndex();
      }, 100);
    }
  }
  
  disconnectedCallback() {
    // Cleanup if needed
    this.classList.remove('sticky-enabled');
    this._cleanupInPlaceZoom();
  }
  
  _setupInPlaceZoom() {
    // Find and setup zoom button for in-place zoom
    const zoomButton = this.querySelector('.product__zoom-button');
    if (zoomButton) {
      // Completely disable PhotoSwipe by removing toggle-button behavior
      zoomButton.removeAttribute('is');
      zoomButton.removeAttribute('aria-controls');
      zoomButton.removeAttribute('aria-expanded');
      
      // Clear any existing event listeners and add our own
      const newButton = zoomButton.cloneNode(true);
      zoomButton.parentNode.replaceChild(newButton, zoomButton);
      
      // Add our zoom in-place functionality
      newButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        this._toggleInPlaceZoom();
      }, true);
      
      console.log('PhotoSwipe desactivado, solo zoom in-place activo');
    }
    
    // Disable PhotoSwipe container to prevent any accidental activation
    const pswp = this.querySelector('.pswp');
    if (pswp) {
      pswp.style.display = 'none';
      pswp.setAttribute('aria-hidden', 'true');
    }
    
    // Listen for image changes (thumbnail clicks, etc.)
    this._setupImageChangeListener();
  }
  
  _setupImageChangeListener() {
    // Listen for Flickity slide changes
    const mediaList = this.querySelector('.product__media-list');
    if (mediaList) {
      // Use MutationObserver to detect when 'is-selected' class changes
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            const target = mutation.target;
            if (target.classList.contains('product__media-item') && target.classList.contains('is-selected')) {
              this._handleImageChange();
            }
          }
        });
      });
      
      // Observe all media items
      const mediaItems = this.querySelectorAll('.product__media-item');
      mediaItems.forEach(item => {
        observer.observe(item, { attributes: true, attributeFilter: ['class'] });
      });
      
      // Store observer for cleanup
      this._imageChangeObserver = observer;
    }
  }
  
  _handleImageChange() {
    if (this.isInPlaceZoomActive) {
      // Update current image when user changes slides during zoom
      setTimeout(() => {
        const newActiveImageWrapper = this.querySelector('.product__media-item.is-selected .product__media-image-wrapper');
        const newActiveImage = newActiveImageWrapper?.querySelector('img');
        
        if (newActiveImage && newActiveImage !== this.currentImage) {
          // Clean up old image
          if (this.currentImage) {
            const oldWrapper = this.currentImage.closest('.product__media-image-wrapper');
            if (oldWrapper) {
              oldWrapper.classList.remove('zoom-container-active');
              oldWrapper.removeEventListener('mousemove', this._handleMouseMove.bind(this));
              oldWrapper.removeEventListener('mouseleave', this._handleMouseLeave.bind(this));
            }
            this.currentImage.style.transform = '';
            this.currentImage.style.transformOrigin = '';
          }
          
          // Setup new image
          this.currentImage = newActiveImage;
          if (newActiveImageWrapper) {
            newActiveImageWrapper.classList.add('zoom-container-active');
            newActiveImageWrapper.addEventListener('mousemove', this._handleMouseMove.bind(this));
            newActiveImageWrapper.addEventListener('mouseleave', this._handleMouseLeave.bind(this));
          }
          
          console.log('Imagen cambiada durante zoom in-place');
        }
      }, 100);
    }
  }
  
  _toggleInPlaceZoom() {
    if (this.isInPlaceZoomActive) {
      this._deactivateInPlaceZoom();
    } else {
      this._activateInPlaceZoom();
    }
  }
  
  _activateInPlaceZoom() {
    console.log('Activando zoom in-place');
    
    // Get current active image
    const activeImageWrapper = this.querySelector('.product__media-item.is-selected .product__media-image-wrapper');
    if (!activeImageWrapper) return;
    
    const activeImage = activeImageWrapper.querySelector('img');
    if (!activeImage) return;
    
    this.currentImage = activeImage;
    this.isInPlaceZoomActive = true;
    
    // Add zoom active class
    this.classList.add('zoom-active');
    activeImageWrapper.classList.add('zoom-container-active');
    
    // Setup mouse tracking
    activeImageWrapper.addEventListener('mousemove', this._handleMouseMove.bind(this));
    activeImageWrapper.addEventListener('mouseleave', this._handleMouseLeave.bind(this));
    
    // Change zoom button icon/state
    const zoomButton = this.querySelector('.product__zoom-button');
    if (zoomButton) {
      zoomButton.classList.add('zoom-active');
      zoomButton.setAttribute('aria-pressed', 'true');
    }
    
    // Show instructions
    this._showZoomInstructions();
  }
  
  _deactivateInPlaceZoom() {
    console.log('Desactivando zoom in-place');
    
    this.isInPlaceZoomActive = false;
    
    // Remove zoom active class
    this.classList.remove('zoom-active');
    
    // Reset all image containers
    const imageWrappers = this.querySelectorAll('.product__media-image-wrapper');
    imageWrappers.forEach(wrapper => {
      wrapper.classList.remove('zoom-container-active');
      wrapper.removeEventListener('mousemove', this._handleMouseMove.bind(this));
      wrapper.removeEventListener('mouseleave', this._handleMouseLeave.bind(this));
      
      const img = wrapper.querySelector('img');
      if (img) {
        img.style.transform = '';
        img.style.transformOrigin = '';
      }
    });
    
    // Reset zoom button state
    const zoomButton = this.querySelector('.product__zoom-button');
    if (zoomButton) {
      zoomButton.classList.remove('zoom-active');
      zoomButton.setAttribute('aria-pressed', 'false');
    }
    
    // Hide instructions
    this._hideZoomInstructions();
    
    this.currentImage = null;
  }
  
  _handleMouseMove(e) {
    if (!this.isInPlaceZoomActive || !this.currentImage) return;
    
    const wrapper = e.currentTarget;
    const rect = wrapper.getBoundingClientRect();
    
    // Calculate mouse position as percentage
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    // Apply zoom and position
    this.currentImage.style.transformOrigin = `${x}% ${y}%`;
    this.currentImage.style.transform = `scale(${this.zoomLevel})`;
  }
  
  _handleMouseLeave(e) {
    if (!this.isInPlaceZoomActive || !this.currentImage) return;
    
    // Reset zoom when mouse leaves
    this.currentImage.style.transform = 'scale(1)';
    this.currentImage.style.transformOrigin = 'center center';
  }
  
  _showZoomInstructions() {
    // Create or show zoom instructions
    let instructions = this.querySelector('.zoom-instructions');
    if (!instructions) {
      instructions = document.createElement('div');
      instructions.className = 'zoom-instructions';
      instructions.innerHTML = `
        <span class="zoom-instructions-text">
          🔍 Mueve el mouse sobre la imagen para hacer zoom. Haz clic en la lupa para salir.
        </span>
      `;
      this.appendChild(instructions);
    }
    instructions.style.display = 'block';
    
    // Auto hide after 3 seconds
    setTimeout(() => {
      if (instructions) {
        instructions.style.opacity = '0';
      }
    }, 3000);
  }
  
  _hideZoomInstructions() {
    const instructions = this.querySelector('.zoom-instructions');
    if (instructions) {
      instructions.style.display = 'none';
      instructions.style.opacity = '1';
    }
  }
  
  _cleanupInPlaceZoom() {
    if (this.isInPlaceZoomActive) {
      this._deactivateInPlaceZoom();
    }
    
    // Cleanup MutationObserver
    if (this._imageChangeObserver) {
      this._imageChangeObserver.disconnect();
      this._imageChangeObserver = null;
    }
  }
  
  _ensurePhotoSwipeZIndex() {
    // Disable PhotoSwipe completely and prevent it from initializing
    const pswp = this.querySelector('.pswp');
    if (pswp) {
      pswp.style.display = 'none';
      pswp.setAttribute('aria-hidden', 'true');
      
      // Remove any PhotoSwipe-related attributes to prevent initialization
      pswp.removeAttribute('tabindex');
      pswp.removeAttribute('role');
    }
    
    // Prevent PhotoSwipe library from loading by overriding LibraryLoader for this context
    if (window.LibraryLoader) {
      const originalLoad = window.LibraryLoader.load;
      window.LibraryLoader.load = function(library) {
        if (library === 'photoswipe') {
          console.log('PhotoSwipe loading blocked - using zoom in-place instead');
          return Promise.resolve();
        }
        return originalLoad.call(this, library);
      };
    }
  }
}

// Register the custom element
window.customElements.define('sticky-product-media', StickyProductMedia);

// Export for potential external usage
window.StickyProductMedia = StickyProductMedia;
